import React, { useState, useContext } from 'react';
import { ServiceResponse } from '../../../../app/services/shared/interfaces';
import DefaultCard from '../../../../components/default/DefaultCard';
import DefaultModal from '../../../../components/default/DefaultModal';
import DefaultToolBar from '../../../../components/default/DefaultToolBar';
import ButtonCreate from '../../../../components/buttons/ButtonCreate';
import ButtonSaveForm from '../../../../components/buttons/ButtonSaveForm';
import ButtonCancelForm from '../../../../components/buttons/ButtonCancelForm';
import ButtonTableAction from '../../../../components/buttons/ButtonTableAction';
import SelectTwo from '../../../../components/SelectTwo';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../../../hooks/useReactConfirmAlert';
import { AppContext } from '../../../../contexts/AppContext';
import usePackingOrderService from '../../../../app/services/hooks/usePackingOrderService';
import { PackingOrderOutputProductForm } from '../../../../app/models/PackingOrder';

type SelectOpt = { id: number | string; name: string };

interface Props {
  packingOrderId: number;
  outputProducts: PackingOrderOutputProductForm[];
  products: SelectOpt[];
  units: SelectOpt[];
  status?: string;
  onReload: () => void;
}

const PackingOrderOutputProductSection = ({
  packingOrderId,
  outputProducts,
  products,
  units,
  status,
  onReload
}: Props) => {
  const locked = status !== undefined && status !== 'CREATED';
  const { showLoading, hideLoading } = useContext(AppContext);
  const { storeOutputProduct, updateOutputProduct, deleteOutputProduct, fetchingOutputProducts } = usePackingOrderService();
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<PackingOrderOutputProductForm>({
    packing_order_id: packingOrderId,
    product_id: undefined,
    quantity: 0,
    unit_id: null
  });

  const productOptions = products.map((p) => ({ value: p.id, label: String(p.name) }));
  const unitOptions = units.map((u) => ({ value: u.id, label: String(u.name) }));

  const formatInt = (value: any) => {
    if (value == null) return '0';
    const n = Number(value);
    return Number.isNaN(n) ? '0' : Math.trunc(n).toLocaleString('es-CL', { maximumFractionDigits: 0 });
  };

  const handleAdd = () => {
    setEditingId(null);
    setForm({ packing_order_id: packingOrderId, product_id: undefined, quantity: 0, unit_id: null });
    setShowAddModal(true);
  };

  const handleEdit = (row: PackingOrderOutputProductForm) => {
    setEditingId(row.id ?? null);
    setForm({
      packing_order_id: packingOrderId,
      product_id: row.product_id,
      quantity: row.quantity ?? 0,
      unit_id: row.unit_id ?? null
    });
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingId(null);
  };

  const handleSave = () => {
    if (!form.product_id || !form.quantity || form.quantity <= 0) {
      toast.warning('Seleccione producto e ingrese cantidad.');
      return;
    }
    const isEditing = editingId != null;
    showLoading?.('loading', isEditing ? 'Actualizando producto resultado...' : 'Agregando producto resultado...');
    if (isEditing) {
      updateOutputProduct(editingId, { product_id: form.product_id, quantity: form.quantity, unit_id: form.unit_id ?? undefined }, {
        onSuccess: (response: ServiceResponse) => {
          hideLoading?.();
          toast.success(response.message);
          handleCloseModal();
          onReload();
        },
        onError: (response: ServiceResponse) => {
          hideLoading?.();
          toast.error(response.message);
        }
      });
    } else {
      storeOutputProduct(
        { ...form, packing_order_id: packingOrderId },
        {
          onSuccess: (response: ServiceResponse) => {
            hideLoading?.();
            toast.success(response.message);
            handleCloseModal();
            onReload();
          },
          onError: (response: ServiceResponse) => {
            hideLoading?.();
            toast.error(response.message);
          }
        }
      );
    }
  };

  const handleDelete = (rowId: number) => {
    useReactConfirmAlert().requestConfirmation({
      title: '¿Eliminar producto resultado?',
      message: 'Se eliminará este ítem de la lista.',
      buttons: {
        confirmButton: {
          label: 'Sí, eliminar',
          onClick: () => {
            deleteOutputProduct(rowId, {
              onSuccess: (response: ServiceResponse) => {
                toast.success(response.message);
                onReload();
              },
              onError: (response: ServiceResponse) => toast.error(response.message)
            });
          }
        },
        cancelButton: { label: 'Cancelar', onClick: () => { return; } }
      }
    });
  };

  return (
    <>
      <DefaultCard>
        <DefaultToolBar
          left={
            <h5 className="mt-0 mb-0 card-title">
              Productos Resultados, cantidad requerida
              {locked && (
                <span className="ms-2 badge bg-secondary" title="La orden ya no está en estado Creado. No se pueden modificar los productos resultado.">
                  <i className="mdi mdi-lock" /> Bloqueado
                </span>
              )}
            </h5>
          }
          right={
            !locked
              ? <ButtonCreate callbackCreate={handleAdd} title="Agregar producto resultado" />
              : undefined
          }
        />
        <p className="text-muted small mb-2">
          Productos que se desean elaborar y la cantidad requerida para cada uno.
        </p>
        <div className="table-responsive">
          <table className="table table-sm table-hover">
            <thead>
              <tr>
                <th>Código</th>
                <th>Producto</th>
                <th className="text-end">Cantidad req.</th>
                <th className="text-end">Producido</th>
                <th className="text-end">Pendiente</th>
                <th>Unidad</th>
                <th style={{ width: 100 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {outputProducts.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-muted">
                    Sin productos resultado. Agregue al menos uno.
                  </td>
                </tr>
              )}
              {outputProducts.map((row) => {
                const qty = Number(row.quantity ?? 0);
                const produced = Number(row.quantity_produced ?? 0);
                const pending = qty - produced;
                return (
                  <tr key={row.id}>
                    <td>
                      <span className="text-muted small">{row.product_code ?? '—'}</span>
                    </td>
                    <td>{row.product_name ?? '-'}</td>
                    <td className="text-end">{formatInt(qty)}</td>
                    <td className="text-end fw-semibold text-success">
                      {produced > 0 ? formatInt(produced) : <span className="text-muted">0</span>}
                    </td>
                    <td className={`text-end fw-semibold ${pending > 0 ? 'text-warning' : pending < 0 ? 'text-danger' : 'text-success'}`}>
                      {formatInt(pending)}
                    </td>
                    <td>{row.unit_name ?? '-'}</td>
                    <td>
                      {!locked ? (
                        <div className="d-flex gap-1">
                          <ButtonTableAction
                            callbackFunction={() => handleEdit(row)}
                            classIcon="mdi mdi-pencil"
                            colorIcon="text-warning"
                            title="Editar"
                          />
                          <ButtonTableAction
                            callbackFunction={() => handleDelete(row.id!)}
                            classIcon="mdi mdi-delete"
                            colorIcon="text-danger"
                            title="Eliminar"
                          />
                        </div>
                      ) : (
                        <span className="text-muted small">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </DefaultCard>

      <DefaultModal
        show={showAddModal}
        handleClose={handleCloseModal}
        title={editingId != null ? 'Editar producto resultado' : 'Agregar producto resultado'}
        backdrop
        showFooter={false}
      >
        <div className="mb-2">
          <label className="col-form-label">Producto</label>
          <SelectTwo
            name="product_id"
            inputValue={form.product_id ?? null}
            options={productOptions}
            onChange={(v: any) => setForm({ ...form, product_id: v?.value })}
            placeholder="Seleccione producto"
          />
        </div>
        <div className="mb-2">
          <label className="col-form-label">Cantidad</label>
          <input
            type="number"
            step="0.01"
            min="0.01"
            className="form-control"
            value={form.quantity ?? ''}
            onChange={(e) => setForm({ ...form, quantity: parseFloat(e.target.value) || 0 })}
          />
        </div>
        <div className="mb-2">
          <label className="col-form-label">Unidad (opcional)</label>
          <SelectTwo
            name="unit_id"
            inputValue={form.unit_id ?? null}
            options={unitOptions}
            onChange={(v: any) => setForm({ ...form, unit_id: v?.value ?? null })}
            placeholder="Seleccione unidad"
          />
        </div>
        <hr />
        <div className="d-flex justify-content-end gap-2">
          <ButtonCancelForm callbackCancel={handleCloseModal} locked={!!fetchingOutputProducts} />
          <ButtonSaveForm callbackSave={handleSave} locked={!!fetchingOutputProducts} />
        </div>
      </DefaultModal>
    </>
  );
};

export default PackingOrderOutputProductSection;
